import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { Container, PageHeader, Spinner } from "@/components/shared";
import { QuoteCard, QuoteActions } from "@/components/QuoteCard";
import { toast } from "sonner";
import { Lightbulb, BookOpen, Film, User, ArrowUpRight, ArrowDown, Volume2, VolumeX } from "lucide-react";
import { ambientEnabled, setAmbientEnabled, startAmbient, stopAmbient } from "@/lib/ambient";

const MEDIA_ICON = { documentaire: Film, film: Film, conference: Film };

export default function QuoteOfDay() {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ambient, setAmbient] = useState(ambientEnabled());
  const bioRef = useRef(null);

  useEffect(() => {
    api.get("/quotes/today").then(({ data }) => setQuote(data))
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);

  // Démarre / coupe l'ambiance selon la préférence, et nettoie en quittant la page.
  useEffect(() => {
    if (ambient) startAmbient();
    return () => stopAmbient();
  }, [ambient]);

  const toggleAmbient = () => {
    const next = !ambient;
    setAmbient(next);
    setAmbientEnabled(next);
    if (!next) stopAmbient();
  };

  const scrollToBio = () => bioRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (loading) return <Container><Spinner label="Citation du jour…" /></Container>;
  if (!quote) return <Container><p className="text-white/40">Aucune citation disponible.</p></Container>;

  const hasMore = quote.bio || quote.books?.length > 0 || quote.media?.length > 0;

  return (
    <Container>
      <div className="flex items-start justify-between gap-4">
        <PageHeader eyebrow="Citation du jour" title="Une pensée, chaque jour"
          subtitle="Inspiration, mémoire et transmission des grandes figures noires." />
        <button data-testid="ambient-toggle" onClick={toggleAmbient}
          title={ambient ? "Couper l'ambiance sonore" : "Activer l'ambiance sonore"}
          className={`shrink-0 mt-1 inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs transition-colors ${ambient ? "border-gold/40 text-gold bg-gold/10" : "border-white/15 text-white/50 hover:text-white"}`}>
          {ambient ? <Volume2 size={15} /> : <VolumeX size={15} />}
          <span className="hidden sm:inline">Ambiance</span>
        </button>
      </div>

      <motion.div className="max-w-2xl mx-auto mt-6"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>
        <QuoteCard quote={quote} animate />

        <motion.div className="mt-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.6 }}>
          <QuoteActions quote={quote} />
        </motion.div>

        {hasMore && (
          <motion.button data-testid="quote-learn-more" onClick={scrollToBio}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.6 }}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-gold/30 text-gold py-2.5 text-sm hover:bg-gold/10 transition-colors">
            <ArrowDown size={15} /> En savoir plus sur cette personnalité
          </motion.button>
        )}

        {quote.why && (
          <div className="card-surface p-5 mt-6 border-gold/20" data-testid="quote-why">
            <div className="flex items-center gap-2 mb-2 text-gold"><Lightbulb size={16} />
              <h3 className="font-display text-base">Pourquoi cette citation est importante ?</h3></div>
            <p className="text-white/70 text-sm leading-relaxed">{quote.why}</p>
          </div>
        )}

        <div ref={bioRef} className="scroll-mt-24">
          {quote.bio && (
            <div className="card-surface p-5 mt-4" data-testid="quote-bio">
              <div className="flex items-center gap-2 mb-2 text-white/80"><User size={16} className="text-gold" />
                <h3 className="font-display text-base">{quote.author}</h3></div>
              <p className="text-white/60 text-sm leading-relaxed">{quote.bio}</p>
              {quote.source && <p className="text-white/30 text-xs mt-2">Source : {quote.source}</p>}
            </div>
          )}
        </div>

        {quote.books?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-display text-base mb-3 flex items-center gap-2"><BookOpen size={16} className="text-gold" /> Ses livres dans la Bibliothèque</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {quote.books.map((b) => (
                <Link key={b.book_id} to={`/bibliotheque/${b.book_id}`} className="group" data-testid={`quote-book-${b.book_id}`}>
                  <div className="aspect-[2/3] rounded-lg overflow-hidden border border-white/10 bg-white/[0.03]">
                    {b.image_url && <img src={b.image_url} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                  </div>
                  <p className="text-white/70 text-xs mt-1.5 line-clamp-2">{b.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {quote.media?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-display text-base mb-3 flex items-center gap-2"><Film size={16} className="text-gold" /> Documentaires & conférences</h3>
            <div className="space-y-2">
              {quote.media.map((m, i) => {
                const Icon = MEDIA_ICON[m.type] || Film;
                return (
                  <a key={i} href={m.url} target="_blank" rel="noreferrer"
                    className="card-surface p-3 flex items-center gap-3 hover:border-gold/30 transition-colors" data-testid={`quote-media-${i}`}>
                    <Icon size={16} className="text-gold shrink-0" />
                    <span className="text-white/80 text-sm flex-1">{m.title}</span>
                    <ArrowUpRight size={15} className="text-white/30" />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-center text-white/25 text-xs mt-8">Revenez demain pour une nouvelle citation ✨</p>
      </motion.div>
    </Container>
  );
}
